import clsx from 'clsx';
import { type ChangeEvent, useId } from 'react';
import type { Color } from './colors';
import type { Size } from './layout';
import { isDefined, isNotDefined } from '@/utils/assertions';

export type ToggleButtonProps = {
  className?: string;
  color?: Color;
  dataE2e?: string;
  disabled?: boolean;
  multiple?: boolean;
  onChange?: (values: string[]) => void;
  options?: ToggleButtonOption[];
  size?: Size;
  values?: string[];
};

export type ToggleButtonOption = {
  value: string;
  label: string;
};

/**
 * SG Bootstrap doc: https://developer.sgmarkets.com/docs/design-system/v5/components/daterange-pickers.html
 *
 * By default
 * - color = "primary"
 * - disabled = false
 * - multiple = false
 * - size = "md"
 *
 * @example
 * <ToggleButton
 *   values={[]}
 *   options={[ { value: "one", label: "One"}]}
 *   onChange={(newValues: string[]) => {}}
 * />
 */
export const ToggleButton = (props: ToggleButtonProps) => {
  const {
    className,
    color = 'primary',
    dataE2e,
    disabled = false,
    multiple = false,
    onChange,
    options = [],
    size = 'md',
    values = [],
  } = props;

  // why min-width: 330 px?
  // because it is the minimum size to display all the end date when the reset button is visible (by default)
  return (
    <div
      className={clsx('btn-group btn-group-toggle', `btn-group-${size}`, className)}
      data-e2e={dataE2e}
      // biome-ignore lint/a11y/useSemanticElements: <explanation>
      role="group"
    >
      {options.map(({ label, value: optionValue }) => (
        <ToggleRadioButton
          key={optionValue}
          color={color}
          currentValues={multiple !== true ? (values.length > 0 ? [values[0]] : []) : values}
          dataE2e={isDefined(dataE2e) ? `${dataE2e}.${optionValue.replace(' ', '_')}` : undefined}
          disabled={disabled}
          label={label}
          multiple={multiple}
          onChange={onChange}
          value={optionValue}
        />
      ))}
    </div>
  );
};

type ToggleRadioButtonProps = {
  color?: Color;
  currentValues: string[];
  dataE2e?: string;
  disabled?: boolean;
  label: string;
  multiple?: boolean;
  onChange?: (values: string[]) => void;
  value: string;
};

const ToggleRadioButton = (props: ToggleRadioButtonProps) => {
  const { color, dataE2e, disabled, multiple, currentValues, value, label, onChange } = props;

  const id = useId();

  const onInputChange = (_event: ChangeEvent<HTMLInputElement>) => {
    if (isNotDefined(onChange)) {
      return;
    }

    if (multiple) {
      const isChecked = currentValues.includes(value);

      onChange(
        isChecked
          ? currentValues.filter(activeValue => activeValue !== value)
          : [...currentValues, value],
      );
    } else {
      onChange([value]);
    }
  };

  const buttonType = multiple ? 'checkbox' : 'radio';

  return (
    <>
      <input
        autoComplete="off"
        checked={currentValues?.includes(value)}
        className="btn-check"
        data-e2e={dataE2e}
        disabled={disabled}
        value={value}
        id={id}
        onChange={onInputChange}
        type={buttonType}
      />

      <label className={`btn btn-toggle-${color} btn-icon-start`} htmlFor={id}>
        <span className="toggle-icon" />
        {label}
      </label>
    </>
  );
};
